/**
 * iOS Screen Orientation Bridge
 * Provides a bridge to the native iOS ScreenOrientationPlugin
 */

import { Capacitor } from '@capacitor/core';

/**
 * Check if the native iOS plugin exists
 */
function pluginExists() {
  return !!(
    Capacitor.Plugins?.ScreenOrientationPlugin || Capacitor.Plugins?.ScreenOrientationPluginLock
  );
}

/**
 * Call native iOS plugin method
 */
async function callNativePlugin(method, options = {}) {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('Native plugin only works on native platforms');
  }

  // Fast fail: check if plugin exists before attempting to call
  if (!pluginExists()) {
    console.warn(
      '[ScreenOrientation] ⚠️ ScreenOrientationPlugin not found, skipping orientation lock',
    );
    return false;
  }

  try {
    // Use Capacitor's native bridge
    const result = await Capacitor.Plugins.ScreenOrientationPlugin[method](options);
    return result;
  } catch (error) {
    // If plugin is not registered, try alternative method
    if (error?.message?.includes('not defined') || error?.message?.includes('undefined')) {
      // Try using Capacitor's native call directly
      const result = await Capacitor.nativePromise('ScreenOrientationPlugin', method, options);
      return result;
    }
    throw error;
  }
}

/**
 * Lock screen orientation on iOS
 */
export async function lockOrientationIOS(orientation) {
  try {
    await callNativePlugin('lock', { orientation });
    console.log(`[ScreenOrientation] ✅ Locked to ${orientation} using iOS native plugin`);
    return true;
  } catch (error) {
    // Handle iPad Stage Manager restriction gracefully
    if (
      error?.message?.includes('windowing mode') ||
      error?.message?.includes('programmatic changes to interface orientation')
    ) {
      console.log(
        '[ScreenOrientation] iPad Stage Manager/multitasking mode - orientation lock not allowed (this is expected)',
      );
      return false; // Return false to allow fallback, but don't log as error
    }
    console.warn('[ScreenOrientation] ⚠️ iOS native plugin failed:', {
      message: error?.message || 'Unknown error',
      error: error,
    });
    return false;
  }
}

/**
 * Unlock screen orientation on iOS
 */
export async function unlockOrientationIOS() {
  try {
    await callNativePlugin('unlock', {});
    console.log('[ScreenOrientation] ✅ Unlocked using iOS native plugin');
    return true;
  } catch (error) {
    // Handle iPad Stage Manager restriction gracefully
    if (
      error?.message?.includes('windowing mode') ||
      error?.message?.includes('programmatic changes to interface orientation')
    ) {
      console.log(
        '[ScreenOrientation] iPad Stage Manager/multitasking mode - orientation unlock not allowed (this is expected)',
      );
      return false; // Return false to allow fallback, but don't log as error
    }
    console.warn('[ScreenOrientation] ⚠️ iOS native plugin failed:', {
      message: error?.message || 'Unknown error',
      error: error,
    });
    return false;
  }
}
